import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  type TemplateRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import {
  MtBottomNavComponent,
  type MtBottomNavItem,
} from '../../design-system/bottom-nav/mt-bottom-nav.component';
import { MtHeroPillComponent } from '../../design-system/hero-pill/mt-hero-pill.component';
import { MtHeroComponent } from '../../design-system/hero/mt-hero.component';
import { MtScreenShellComponent } from '../../design-system/screen-shell/mt-screen-shell.component';
import { type JourneyDates, formatGregorian, formatHijri } from '../../shared/dates/journey-dates';
import {
  type PassphraseCreated,
  PassphraseCreationComponent,
  type PassphraseCreationDestination,
} from '../security/passphrase-creation.component';
import { biometricOf, biometricPrompt, kindWord } from './biometric';
import { exitToleranceFor } from './exit-tolerance';
import { Guardianship } from './guardianship';
import { JourneyDashboard } from './journey-dashboard';
import {
  DOCUMENTS_TAB,
  JourneyNavigation,
  SETTINGS_TAB,
  WASI_TAB,
  WISHES_TAB,
} from './journey-navigation';
import { JourneyOnline } from './journey-online';
import { JourneyStart } from './journey-start';
import { JourneyStore, type ScreenChrome } from './journey.store';
import type { JourneyView } from './journey.types';
import { PrivateCollections } from './private-collections';
import { PublicJourneyConfig, PublicJourneyUseCases } from './public-journey.dependencies';
import { SpiritualBalance } from './spiritual-balance';

/*
 * Creating the phrase draws its own screen, band and footer included, whichever door led to it:
 * the rest of the journey steps aside until it is done or left. It is needed once in a lifetime,
 * so it is fetched when first opened rather than carried in the first download.
 */
const PASSPHRASE_CREATION_SCREEN =
  '@if (view === "passphrase-creation") { @defer (on immediate) { ' +
  '<app-passphrase-creation [returnTo]=creationOrigin [destination]=creationDestination ' +
  '[banner]=creationBanner (created)=onPassphraseCreated($event) ' +
  '[offer]=creationOffer (offerAnswered)=answerCreationOffer($event) ' +
  '(finished)=finishPassphraseCreation() (left)=leavePassphraseCreation() /> } } @else {';

const SHELL_START =
  '<app-screen-shell [ground]=shellGround [showHeader]=!heroVisible [brand]=text.brand ' +
  '[showBack]=showBack [backLabel]=backLabel [screenKey]=screenKey ' +
  '[showFooter]=footerVisible [bottomNav]=showBottomNav (back)=goBack()>';

/* The band: the words the screen on show asked for, and whatever it put under them. */
const HERO =
  '@if (heroVisible) { ' +
  '<app-hero mt-hero [variant]=heroVariant [brand]=heroBrand [org]=heroOrg ' +
  '[gregorian]=heroGregorian [hijri]=heroHijri [title]=heroTitle [lead]=heroLead ' +
  '[kicker]=heroKicker [bars]=heroBars [showBack]=heroBack [backLabel]=backLabel (back)=goBack()>' +
  '@if (heroPill) { <app-hero-pill mt-hero-aside [label]=heroPill [icon]=heroPillIcon ' +
  '[pressable]=heroPillPressable (pressed)=lockPrivate() /> }' +
  '@if (heroAside; as aside) { <ng-container [ngTemplateOutlet]=aside /> }' +
  '</app-hero> }';

/*
 * Above everything, on every screen, until the scholar has validated the religious content: the
 * shell gives it a row of its own, over the band and clear of the notch.
 */
const VALIDATION_BANNER =
  '@if (showsValidationBanner) { <aside mt-banner class=validation-banner role=status>' +
  '{{ text.validationBanner }}</aside> }';

/*
 * One screen at a time, the one the address names. The routed screen is drawn beside the outlet,
 * inside the frame; the outlet itself is only an anchor.
 */
const SCREENS = '<router-outlet />';

/* The footer: what went wrong, whatever the screen put there, and the bar. */
const FOOTER =
  '<div mt-footer>' +
  '@if (errorMessage) { <p class=error role=alert>{{ errorMessage }}</p> }' +
  '@if (screenFooter; as footer) { <ng-container [ngTemplateOutlet]=footer /> }' +
  '@if (showBottomNav) { <app-bottom-nav [items]=navItems [active]=activeTab ' +
  '[label]=text.navLabel [moreLabel]=text.moreTitle [moreActive]=isMoreTab ' +
  '(selected)=selectTab($event) (moreSelected)=openMoreTab() /> }' +
  '</div></app-screen-shell> }';

/** The screens that belong to More: it stays lit while the reader is anywhere under it. */
const MORE_VIEWS: ReadonlySet<JourneyView> = new Set<JourneyView>([
  'more',
  'about',
  'support',
  'contact',
]);

/** The screens reached through the bar: nothing sits behind them to go back to. */
const ROOT_VIEWS: ReadonlySet<JourneyView> = new Set<JourneyView>([
  'dashboard',
  'executor',
  'documents',
  'settings',
  'more',
]);

/** The part screen is a level of the wishes tab, so the bar stays with it, as with these. */
const VIEWS_WITH_BAR: ReadonlySet<JourneyView> = new Set<JourneyView>([
  ...ROOT_VIEWS,
  'dashboard-part',
  'settings-language',
  'security',
  'about',
  'contact',
]);

const SETTINGS_VIEWS: ReadonlySet<JourneyView> = new Set<JourneyView>([
  'settings',
  'settings-language',
  'security',
  'security-change',
  'security-forgot',
  'backup',
  'backup-ready',
  'backup-online',
  'backup-conflict',
]);

/** The tab lit for a screen: the one it was reached through. */
export const activeTabOf = (view: JourneyView): string => {
  // Under More, no tab is current: the round button carries the mark instead.
  if (MORE_VIEWS.has(view)) return '';
  if (view === 'executor' || view === 'executor-notice' || view === 'executor-access') {
    return WASI_TAB;
  }
  if (view === 'documents' || view === 'testament-preview') return DOCUMENTS_TAB;
  if (SETTINGS_VIEWS.has(view)) return SETTINGS_TAB;
  return WISHES_TAB;
};

/** Everything behind the phrase: when the space closes, the reader is taken out of it. */
const PRIVATE_VIEWS: ReadonlySet<JourneyView> = new Set<JourneyView>([
  'private-collection',
  'private-item',
  'spiritual-balance',
  'guardianship',
  'estate',
  'unlock',
]);

/**
 * The journey is the frame — the band, the footer, the bar, the way back — around one screen at a
 * time: the one its address names. The screens draw themselves and tell the frame what to wear;
 * what they share lives in the services provided with the journey's route.
 */
@Component({
  selector: 'app-public-journey',
  standalone: true,
  imports: [
    MtBottomNavComponent,
    MtHeroComponent,
    MtHeroPillComponent,
    MtScreenShellComponent,
    NgTemplateOutlet,
    PassphraseCreationComponent,
    RouterOutlet,
  ],
  template: PASSPHRASE_CREATION_SCREEN + SHELL_START + VALIDATION_BANNER + HERO + SCREENS + FOOTER,
  styleUrl: './public-journey.component.scss',
})
export class PublicJourneyComponent implements OnInit {
  private readonly useCases = inject(PublicJourneyUseCases);
  private readonly config = inject(PublicJourneyConfig);
  private readonly i18n = inject(TranslocoService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentRef = inject(DOCUMENT);
  private readonly store = inject(JourneyStore);
  private readonly collections = inject(PrivateCollections);
  private readonly spiritual = inject(SpiritualBalance);
  private readonly guardianship = inject(Guardianship);
  private readonly dashboard = inject(JourneyDashboard);
  private readonly navigation = inject(JourneyNavigation);
  private readonly start = inject(JourneyStart);
  /** Made with the journey, so the will goes online by itself whichever screen is shown. */
  private readonly online = inject(JourneyOnline);

  constructor() {
    this.store.whenLocked(() => this.handleSessionLock(), this.destroyRef);
    // Leaving the app gives the space fifteen minutes, not a lock at once: see ExitTolerance.
    const tolerance = exitToleranceFor(this.store, this.documentRef);
    tolerance.watch();
    // Reading counts as much as typing: a scroll, a wheel, a swipe keep the space open. Once a
    // second at most, so a long scroll does not restart the clock a hundred times.
    let touchedAt = 0;
    const activity = (): void => {
      const now = Date.now();
      if (now - touchedAt < 1_000) return;
      touchedAt = now;
      this.store.session.touch();
    };
    const events = ['pointerdown', 'keydown', 'input', 'wheel', 'scroll', 'touchmove'];
    for (const type of events) {
      this.documentRef.addEventListener(type, activity, { capture: true, passive: true });
    }
    this.destroyRef.onDestroy(() => {
      tolerance.stop();
      for (const type of events) this.documentRef.removeEventListener(type, activity, true);
    });
  }

  async ngOnInit(): Promise<void> {
    // Every label here is read imperatively through translate(), so a catalogue that lands after
    // the switch — at boot from storage, or on any later change — needs a pass to become visible.
    this.i18n.events$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.changeDetector.markForCheck();
    });
    await this.start.start();
    this.changeDetector.markForCheck();
  }

  protected get view(): JourneyView {
    return this.store.view();
  }

  protected get errorMessage(): string {
    return this.store.errorMessage();
  }

  protected get creationOrigin() {
    return this.store.creationOrigin();
  }

  protected get showsValidationBanner(): boolean {
    return this.config.validationBanner;
  }

  protected get screenKey(): string {
    return `${this.view}:${this.screenChrome?.step?.() ?? 0}:${this.store.activePrivateSectionId()}`;
  }

  /**
   * Every screen but the dashboard ends on a primary action. The dashboard routes out through
   * its own rows and document buttons, so its footer would otherwise be an empty bar — it stays
   * only to carry an error.
   */
  protected get footerVisible(): boolean {
    if (this.errorMessage.length > 0) return true;
    if (this.view === 'dashboard') return this.showBottomNav;
    // The ledger's one action is the pill in the band now: its footer would be an empty bar.
    // The wasi's door keeps its buttons under its fields, where a phone's keyboard leaves them.
    return this.view !== 'private-collection' && this.view !== 'wasi';
  }

  protected get isMoreTab(): boolean {
    return MORE_VIEWS.has(this.view);
  }

  protected get showBottomNav(): boolean {
    return VIEWS_WITH_BAR.has(this.view);
  }

  private get isRootTab(): boolean {
    return ROOT_VIEWS.has(this.view);
  }

  protected get activeTab(): string {
    return activeTabOf(this.view);
  }

  protected get navItems(): readonly MtBottomNavItem[] {
    return [
      { id: WISHES_TAB, label: this.translate('nav.wishes'), icon: 'wishes' },
      { id: WASI_TAB, label: this.translate('nav.wasi'), icon: 'wasi' },
      { id: DOCUMENTS_TAB, label: this.translate('nav.documents'), icon: 'documents' },
      { id: SETTINGS_TAB, label: this.translate('nav.settings'), icon: 'settings' },
    ];
  }

  protected selectTab(id: string): void {
    this.navigation.selectTab(id);
  }

  protected openMoreTab(): void {
    this.store.session.touch();
    this.store.go('more');
  }

  /**
   * The private part that asked for the phrase, named as the dashboard names it: the door shows
   * it, and the last button of the creation leads back to it.
   */
  protected get creationDestination(): PassphraseCreationDestination | null {
    if (this.creationOrigin !== 'private-section') return null;
    const row = 'dashboard.rows.' + this.store.activePrivateSectionId();
    return {
      kicker: this.dashboard.activePart()?.name ?? '',
      title: this.translate(row + '.label'),
      lead: this.translate(row + '.desc'),
    };
  }

  protected get creationBanner(): string {
    return this.showsValidationBanner ? this.text.validationBanner : '';
  }

  /**
   * The phrase exists: the private space opens now, while the creation screen still says so. The
   * reader moves on when they choose to, with `finished`.
   */
  protected onPassphraseCreated(created: PassphraseCreated): void {
    this.store.draft.set(created.draft);
    this.store.openSession(created.passphrase, created.vault);
    this.changeDetector.markForCheck();
    void this.prepareCreationOffer();
  }

  /**
   * On a phone that can, the face or the finger is offered on the last screen of the creation,
   * while the phrase has just been typed (W12): asked, never turned on unasked. Not on the web.
   */
  protected creationOffer: { readonly title: string; readonly why: string } | null = null;

  private async prepareCreationOffer(): Promise<void> {
    this.creationOffer = null;
    const biometric = await biometricOf(this.useCases);
    if (biometric === null) return;
    biometric.phraseOpened();
    const kind = await biometric.offer(true).catch(() => null);
    if (kind === null) return;
    const word = kindWord(kind);
    this.creationOffer = {
      title: this.translate(`biometric.offerTitle.${word}`),
      why: this.translate(`biometric.why.${word}`),
    };
    this.changeDetector.markForCheck();
  }

  /** « Activer » seals the phrase the session holds; « Plus tard » waits thirty days. */
  protected async answerCreationOffer(accepted: boolean): Promise<void> {
    this.creationOffer = null;
    const biometric = await biometricOf(this.useCases);
    if (biometric !== null) {
      if (accepted && this.store.session.isUnlocked) {
        const phrase = this.store.session.encryption(this.config.keyDerivation).passphrase;
        await biometric.enroll(phrase, biometricPrompt(this.i18n)).catch(() => false);
      } else if (!accepted) {
        biometric.decline();
      }
    }
    await this.finishPassphraseCreation();
  }

  /** Back to wherever the phrase was asked for: the private part, or Settings. */
  protected async finishPassphraseCreation(): Promise<void> {
    if (this.creationOrigin !== 'private-section') {
      this.leaveCreationForOrigin();
      return;
    }
    const session = this.store.session;
    if (!session.isUnlocked) {
      // Closed again while the last screen sat open: the part asks for the phrase, as any would.
      this.navigation.openPrivateDoor({ replace: true });
      return;
    }
    this.store.busy.set(true);
    this.store.errorMessage.set('');
    try {
      await this.navigation.preparePrivateTarget();
      session.touch();
      this.navigation.enterPrivateTarget(session.requireVault());
    } catch (error: unknown) {
      this.store.enterDashboard();
      this.store.fail(error, this.translate('errors.unexpected'));
    } finally {
      this.store.busy.set(false);
      this.changeDetector.markForCheck();
    }
  }

  /** Left before the end: nothing was written, and the reader is back where they came from. */
  protected leavePassphraseCreation(): void {
    this.leaveCreationForOrigin();
  }

  private leaveCreationForOrigin(): void {
    if (this.creationOrigin === 'settings') {
      this.store.go('security', { replace: true });
    } else if (this.creationOrigin === 'backup') {
      this.store.go('backup', { replace: true });
    } else if (this.creationOrigin === 'documents') {
      this.store.go('documents', { replace: true });
    } else {
      this.store.enterDashboard({ replace: true });
    }
    this.changeDetector.markForCheck();
  }

  /** The root tabs are reached through the bar, never behind a back arrow. */
  protected get showBack(): boolean {
    return this.view !== 'home' && !this.isRootTab && this.screenAllowsBack;
  }

  /** A screen with nothing behind it says so, and its arrow is left out. */
  private get screenAllowsBack(): boolean {
    return this.screenChrome?.canGoBack?.() ?? true;
  }

  protected get backLabel(): string {
    return this.translate('actions.back');
  }

  /**
   * The header stands in for the shell bar on every screen that opens on it. The first arrival
   * keeps the bar, which carries only the brand.
   */
  protected get heroVisible(): boolean {
    return this.view !== 'home';
  }

  /** The one screen that takes the whole viewport on the green: the very first arrival. */
  protected get shellGround(): 'light' | 'jardin' {
    return this.view === 'home' ? 'jardin' : 'light';
  }

  /**
   * The four tabs share one band, down to the dates: moving between them changes the content and
   * never the frame. A screen one level in gets the shallower one, with a back arrow instead of
   * the brand.
   */
  protected get heroVariant(): 'root' | 'compact' {
    return this.isRootTab ? 'root' : 'compact';
  }

  protected get heroBrand(): string {
    return this.isRootTab ? this.text.brand : '';
  }

  protected get heroOrg(): string {
    return this.isRootTab ? this.text.dashboardOrg : '';
  }

  protected get heroGregorian(): string {
    return this.isRootTab ? this.journeyDates.gregorian : '';
  }

  protected get heroHijri(): string {
    return this.isRootTab ? this.journeyDates.hijri : '';
  }

  protected get heroBack(): boolean {
    return !this.isRootTab && this.screenAllowsBack;
  }

  /** What the screen on show asked the frame to wear, once it has asked. */
  private get screenChrome(): ScreenChrome | null {
    const chrome = this.store.chrome();
    return chrome !== null && chrome.view === this.view ? chrome : null;
  }

  protected get screenFooter(): TemplateRef<unknown> | null {
    return this.screenChrome?.footer?.() ?? null;
  }

  protected get heroAside(): TemplateRef<unknown> | null {
    return this.screenChrome?.aside?.() ?? null;
  }

  protected get heroTitle(): string {
    return this.screenChrome?.title() ?? '';
  }

  protected get heroLead(): string {
    return this.screenChrome?.lead?.() ?? '';
  }

  /** Where the screen sits: its part, whether it is private. Never what the screen is. */
  protected get heroKicker(): string {
    const chrome = this.screenChrome;
    if (chrome?.kicker !== undefined) return chrome.kicker();
    // A ledger names the part it sits in: the journey knows the parts, and says it.
    if (this.view === 'private-collection') {
      const part = this.dashboard.activePart()?.name ?? this.collections.label();
      return part + ' · ' + this.text.privateEyebrow;
    }
    return '';
  }

  protected get heroBars(): readonly boolean[] {
    return this.screenChrome?.bars?.() ?? [];
  }

  /**
   * The state of the private space, shown where it applies. On a collection it is also the way
   * to close it again — the same `lock` the footer used to carry, moved to the state it changes.
   */
  protected get heroPill(): string {
    const view = this.view;
    if (view === 'private-collection') return this.text.lockAction;
    if (
      view === 'private-item' ||
      view === 'guardianship' ||
      view === 'estate' ||
      view === 'spiritual-balance'
    ) {
      return this.text.privateEyebrow;
    }
    if (view === 'dashboard-part' && this.store.session.isUnlocked) {
      return this.text.privateSpaceOpen;
    }
    return '';
  }

  protected get heroPillIcon(): 'locked' | 'unlocked' {
    return this.store.session.isUnlocked ? 'unlocked' : 'locked';
  }

  protected get heroPillPressable(): boolean {
    return this.view === 'private-collection';
  }

  /**
   * Both dates, worked out on the device. The Hijri one comes from the app's single Umm al-Qura
   * adapter rather than from a second conversion here, and is worded by the active catalogue:
   * Intl writes "rabia ath-thani 1448 AH", which is neither the app's transliteration nor
   * anything a reader would recognise. A conversion that fails leaves the Gregorian date alone.
   *
   * Recomputed per render rather than cached: the app is open across midnight often enough.
   */
  private get journeyDates(): JourneyDates {
    return Object.freeze({
      gregorian: formatGregorian(new Date(), this.i18n.getActiveLang()),
      hijri: this.hijriToday(),
    });
  }

  /** An engine that cannot do the conversion leaves the Gregorian date to stand on its own. */
  private hijriToday(): string {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return formatHijri(
        this.useCases.todayInHijri.execute({ timeZone }),
        this.translateList('dashboard.hijriMonths'),
        this.i18n.getActiveLang(),
      );
    } catch {
      return '';
    }
  }

  protected get text() {
    return {
      brand: this.translate('brand'),
      dashboardOrg: this.translate('dashboard.org'),
      lockAction: this.translate('private.lockAction'),
      moreTitle: this.translate('more.title'),
      navLabel: this.translate('nav.label'),
      privateEyebrow: this.translate('private.eyebrow'),
      privateSpaceOpen: this.translate('private.spaceOpen'),
      validationBanner: this.translate('review.banner'),
    };
  }

  protected lockPrivate(): void {
    this.store.session.lock();
  }

  private handleSessionLock(): void {
    this.collections.forget();
    this.spiritual.forget();
    this.guardianship.forget();
    // Locking leaves the private space altogether, so it returns to the top of the tab rather
    // than to the part last opened — that is the only screen carrying the reassurance notice.
    this.store.activeDashboardPart.set(null);
    if (PRIVATE_VIEWS.has(this.view)) this.store.enterDashboard();
    this.changeDetector.markForCheck();
  }

  /** The screen that knows better says where back leads; the frame knows the rest. */
  protected goBack(): void {
    this.store.errorMessage.set('');
    const back = this.screenChrome?.back;
    if (back !== undefined) {
      back();
      return;
    }
    const session = this.store.session;
    switch (this.view) {
      case 'support':
        this.store.go(this.store.supportReturnView());
        return;
      case 'about':
      case 'contact':
        session.touch();
        this.store.go('more');
        return;
      case 'settings-language':
      case 'security':
        session.touch();
        this.store.go('settings');
        return;
      case 'backup':
        session.touch();
        this.store.go(this.store.backupReturnView());
        return;
      case 'security-forgot':
      case 'security-change':
        session.touch();
        this.store.go('security');
        return;
      case 'private-collection':
      case 'spiritual-balance':
      case 'guardianship':
      case 'estate':
        session.touch();
        this.store.enterDashboard();
        return;
      case 'funeral':
        this.store.enterDashboard();
        return;
      case 'executor-notice':
      case 'executor-access':
        session.touch();
        this.store.go('executor');
        return;
      case 'in-your-country':
      case 'testament-preview':
        this.store.go('documents');
        return;
      case 'emergency':
        if (this.store.emergencyReturnView() === 'documents') {
          this.store.go('documents');
          return;
        }
        this.store.enterDashboard();
        return;
      default:
        this.store.go('home');
    }
  }

  private translate(key: string, params?: object): string {
    return this.i18n.translate(key, params);
  }

  /**
   * A catalogue entry that is a list rather than a sentence. Transloco hands back whatever the
   * catalogue holds, so anything that is not an array of strings is treated as absent — a header
   * missing its month names shows the Gregorian date alone rather than a gap.
   */
  private translateList(key: string): readonly string[] {
    const value: unknown = this.i18n.translate(key);
    if (!Array.isArray(value)) return [];
    return value.every((entry) => typeof entry === 'string') ? (value as string[]) : [];
  }
}
